import { useState, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';
import { EducationForm } from '.';
import { LoadingSpinner } from '../layout/LoadingSpinner';
import type { Education, CreateEducationRequest } from '../../types/profile';
import type { Education as UnifiedEducation } from '../../services/unified/types';

// Adapter function to convert unified API education to component education
const adaptEducation = (edu: UnifiedEducation): Education => ({
  id: edu.id,
  institution: edu.institution,
  degree: edu.degree,
  field: edu.field_of_study,
  startDate: edu.start_date,
  endDate: edu.end_date,
  current: !edu.end_date, // If no end date, assume current
  description: edu.description,
});

// Adapter function to convert component education request to unified API format
const adaptEducationRequest = (req: CreateEducationRequest) => ({
  institution: req.institution,
  degree: req.degree,
  field_of_study: req.field,
  start_date: req.startDate,
  end_date: req.current ? undefined : req.endDate,
  description: req.description,
});

export function EducationSection() {
  const [education, setEducation] = useState<Education[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEducation, setEditingEducation] = useState<Education | null>(null);

  useEffect(() => {
    loadEducation();
  }, []);

  const loadEducation = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await unifiedApiService.profile.getEducation();
      setEducation(data.map(adaptEducation));
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load education');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingEducation(null);
    setShowForm(true);
  };

  const handleEdit = (edu: Education) => {
    setEditingEducation(edu);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this education entry?')) {
      return;
    }

    try {
      await unifiedApiService.profile.deleteEducation(id);
      setEducation(prev => prev.filter(edu => edu.id !== id));
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to delete education');
    }
  };

  const handleFormSubmit = async (educationData: CreateEducationRequest) => {
    if (editingEducation) {
      const updated = await unifiedApiService.profile.updateEducation(
        editingEducation.id, 
        adaptEducationRequest(educationData)
      );
      setEducation(prev => prev.map(edu => edu.id === updated.id ? adaptEducation(updated) : edu));
    } else {
      const created = await unifiedApiService.profile.createEducation(adaptEducationRequest(educationData));
      setEducation(prev => [...prev, adaptEducation(created)]);
    }
    setShowForm(false);
    setEditingEducation(null);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingEducation(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">Education</h2>
          <p className="text-sm text-gray-600">
            Add your educational background and qualifications.
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Education
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <EducationForm
            education={editingEducation}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {education.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No education entries</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding your educational background.</p>
          <div className="mt-6">
            <button
              onClick={handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Add Education
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {education.map((edu) => (
            <div key={edu.id} className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{edu.degree}</h3>
                    {edu.current && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-md font-medium text-blue-600 mb-1">{edu.institution}</p>
                  {edu.field && (
                    <p className="text-sm text-gray-600 mb-2">Field of Study: {edu.field}</p>
                  )}
                  <p className="text-sm text-gray-600 mb-3">
                    {formatDate(edu.startDate)} - {edu.current ? 'Present' : formatDate(edu.endDate!)}
                  </p>
                  {edu.gpa && (
                    <p className="text-sm text-gray-600 mb-2">GPA: {edu.gpa}</p>
                  )}
                  {edu.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{edu.description}</p>
                  )}
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(edu)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(edu.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}